import { useState } from 'react';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import './header-animation.scss';
import { useGetAccountsStore, useGetStatusStore } from '@lib/store';
import { RoutePaths, SessionStorageUtils } from '@shared/utils';
import dayjs from 'dayjs';
import { Logo, Logout, MessageIndicator } from '@shared/widgets';
import clsx from 'clsx';

interface MenuType {
  link: string;
  name: string;
}

interface HeaderType {
  menu: MenuType[];
}

export const Header = (props: HeaderType) => {
  const accountsStore = useGetAccountsStore();
  const statusStore = useGetStatusStore();
  const location = useLocation();
  const params = useParams<{ id: string }>();

  const icaState = SessionStorageUtils.getIcaState();

  const [cardholderVisible, setCardholderVisible] = useState(false);

  const hideCardholder = () => {
    setTimeout(() => {
      setCardholderVisible(false);
    }, 200);
  };

  const formatDate = (value: string) => {
    if (value) return dayjs(value).format('ddd, DD.MM.YY [AT] hh:mm A');
    else return 'Never';
  };

  return (
    <div className='main-menu-animation fixed top-0 left-0 z-1000 h-20 w-full bg-[#131416] shadow-[12px_12px_24px_0_rgba(0,0,0,0.23),_inset_0_2px_0_0_#343434]'>
      <div className='mx-auto my-0 flex h-full w-full max-w-[1294px] items-center justify-start overflow-visible px-5'>
        <div className='mr-20 flex h-full items-start justify-start not-xl:mr-0'>
          <div
            className={clsx(
              'menu-logo-animation relative flex h-full w-auto flex-[0_0_80px] items-center justify-start pr-4',
              cardholderVisible ? 'logo-swipe' : '',
            )}
          >
            <div className='menu-logo__inner relative top-0.5 z-10 w-[120px]'>
              <div className='relative'>
                <Logo />
              </div>
            </div>
          </div>
          <div className='flex h-full w-auto flex-[0_0_295px] items-start justify-start not-xl:shrink' tabIndex={0} onBlur={hideCardholder}>
            <div className='relative flex h-20 w-full items-start justify-center text-[#888] uppercase'>
              <div className='relative z-110 flex h-[inherit] flex-col justify-center !p-1 px-1 pt-3 pb-1 select-none'>
                <div className='cardholder__name-animation relative flex items-center justify-start'>
                  <div className='text-[15px] font-bold text-[#f2f2f2] not-xl:text-sm not-xl:font-semibold'>{icaState?.user?.name}</div>
                </div>
                <div className='last-enter-animation mt-0.5 text-xs font-semibold text-[#888] not-xl:text-[10px] not-xl:font-semibold'>
                  <span className='mr-1'>Last login:</span>
                  <span>{formatDate(icaState?.user?.last_login)}</span>
                </div>
                <div className='last-enter-animation mt-0.5 text-xs font-semibold text-[#888] not-xl:text-[10px] not-xl:font-semibold'>
                  <span className='mr-1'>Status:</span>
                  <span>{statusStore.data?.OperationalStatus}</span>
                </div>
              </div>
              <div className='absolute inset-0 z-[100] flex h-[150px] scale-0 items-center justify-center bg-gradient-to-b from-[#262626] to-[#272727] opacity-0 shadow-[12px_12px_40px_0_rgba(0,0,0,0.23),_inset_0_2px_0_0_#343434] transition-opacity duration-400 ease-out' />
            </div>
          </div>
        </div>
        <div className='flex w-full flex-[0_1_64%] items-center justify-between'>
          <div className='flex h-20 w-full items-start justify-around'>
            <Logout className='menu-item-animation relative flex h-20 w-[100px] cursor-pointer flex-col items-center justify-around border-l border-[hsla(0,0%,57%,0.2)] py-2' />
            {accountsStore.data[0] &&
              props.menu.map((item) => {
                let linkTo = item.link;
                if (item.link.includes(':id')) {
                  linkTo = item.link.replace(':id', params.id || accountsStore.data[0].Id);
                }
                const isActive = location.pathname === linkTo;
                return (
                  <NavLink
                    to={linkTo}
                    className={clsx(
                      isActive ? 'menu-item--current text-white' : '',
                      'menu-item-animation relative z-10 flex h-full flex-1 cursor-pointer items-center justify-center overflow-hidden text-sm font-semibold whitespace-nowrap text-[#888] uppercase no-underline',
                    )}
                    key={item.link}
                  >
                    <div className='.menu-text-animation relative z-50 select-none'>{item.name}</div>
                  </NavLink>
                );
              })}
          </div>
          <NavLink to={RoutePaths.messages.onNavigate({ dir: 'inbox' })} className='relative -mr-4 block'>
            <MessageIndicator />
          </NavLink>
        </div>
      </div>
    </div>
  );
};
